import { useEffect, useState } from "react";
import { getJson, AuthError } from "../lib/auth";
import { Card, Spinner, ErrorBanner } from "../components";
import { theme, fontStack } from "../theme";

/**
 * STATISTIQUES — écran secondaire (demande d'Henri, 3 août : « le nombre de
 * connexions et autres en second plan pour le moment, puis elle est pas belle,
 * on pourrait avoir des dashboards »).
 *
 * L'ancienne version déversait la réponse brute de l'API : des clés techniques
 * passées à la moulinette d'un `humanize()`, puis un tableau de trente lignes de
 * chiffres. On ne lisait rien. Ici : quelques chiffres NOMMÉS en grand, et une
 * courbe d'activité — de quoi répondre à « est-ce que ça vit ? » en un coup d'œil.
 *
 * Le rendu reste DÉFENSIF : l'API peut renvoyer des clés qu'on ne connaît pas
 * (elles s'affichent en fin de liste plutôt que de disparaître) ou aucune donnée.
 */
interface Analytics {
  aggregates?: Record<string, unknown>;
  stats?: Array<Record<string, unknown>>;
  [k: string]: unknown;
}

/** Les chiffres qu'on sait NOMMER, dans l'ordre où ils comptent. */
const KNOWN: Array<{ key: string; label: string; hint: string }> = [
  { key: "averageDau", label: "Personnes actives", hint: "en moyenne par jour" },
  { key: "totalMessages", label: "Messages échangés", hint: "sur la période" },
  { key: "avgTimePerDauSeconds", label: "Temps par personne", hint: "chaque jour" },
  { key: "avgTimePerSessionSeconds", label: "Durée d'une conversation", hint: "en moyenne" },
  { key: "periodDays", label: "Période observée", hint: "jours de données" },
];

const DURATION_KEYS = new Set(["avgTimePerDauSeconds", "avgTimePerSessionSeconds"]);

/** Des secondes en « 4 min 30 » : personne ne lit « 270 ». */
function humanDuration(sec: number): string {
  if (sec < 60) return `${Math.round(sec)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return s ? `${m} min ${s}` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60}`;
}

function humanize(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function Dashboard({ onAuthLost }: { onAuthLost: () => void }) {
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getJson<Analytics>("/v1/admin/analytics?limitDays=30")
      .then((d) => alive && setData(d))
      .catch((e) => {
        if (e instanceof AuthError && e.status === 401) return onAuthLost();
        if (alive) setError(e instanceof Error ? e.message : "Erreur");
      });
    return () => {
      alive = false;
    };
  }, [onAuthLost]);

  if (error) return <ErrorBanner message={error} />;
  if (!data) return <Spinner label="Chargement des statistiques…" />;

  const aggregates = data.aggregates ?? {};
  const stats = Array.isArray(data.stats) ? data.stats : [];

  const known = KNOWN.filter((k) => k.key in aggregates);
  const extras = Object.keys(aggregates).filter((k) => !KNOWN.some((x) => x.key === k));

  // Série d'activité pour la courbe : on prend le nombre de messages par jour.
  const series = stats
    .map((r) => ({ date: String(r.date ?? ""), value: num(r.messageCount) ?? 0 }))
    .filter((p) => p.date)
    .slice(-30);
  const max = Math.max(1, ...series.map((p) => p.value));

  return (
    <div>
      <h1 style={{ fontFamily: fontStack.uiSemiBold, fontSize: 24, marginBottom: 4 }}>Statistiques</h1>
      <p style={{ color: theme.ink3, fontSize: 13.5, marginBottom: 20 }}>
        Vue d'ensemble des 30 derniers jours. Pour traiter les dossiers, allez dans « Dossiers ».
      </p>

      {known.length === 0 && extras.length === 0 ? (
        <Card>
          <div style={{ color: theme.ink3 }}>Aucune donnée d'activité pour l'instant.</div>
        </Card>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
            gap: 14,
            marginBottom: 22,
          }}
        >
          {known.map((k) => {
            const v = num(aggregates[k.key]);
            return (
              <Card key={k.key} style={{ padding: 18 }}>
                <div style={{ color: theme.ink3, fontSize: 12.5 }}>{k.label}</div>
                <div
                  style={{
                    fontFamily: fontStack.uiSemiBold,
                    fontSize: 30,
                    color: theme.accent,
                    lineHeight: 1.2,
                    marginTop: 6,
                  }}
                >
                  {v === null
                    ? "—"
                    : DURATION_KEYS.has(k.key)
                      ? humanDuration(v)
                      : v.toLocaleString("fr-FR")}
                </div>
                {k.hint ? (
                  <div style={{ color: theme.ink3, fontSize: 11.5, marginTop: 2 }}>{k.hint}</div>
                ) : null}
              </Card>
            );
          })}
          {/* Clés inconnues : affichées quand même, mais discrètement — mieux vaut
              une donnée mal nommée qu'une donnée disparue. */}
          {extras.map((k) => (
            <Card key={k} style={{ padding: 18 }}>
              <div style={{ color: theme.ink3, fontSize: 12.5 }}>{humanize(k)}</div>
              <div style={{ fontFamily: fontStack.uiSemiBold, fontSize: 22, marginTop: 6 }}>
                {String(aggregates[k] ?? "—")}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Courbe d'activité : des barres, pas un tableau de trente lignes. */}
      {series.length > 1 && (
        <Card>
          <div style={{ fontFamily: fontStack.uiSemiBold, fontSize: 13, color: theme.ink2, marginBottom: 14 }}>
            MESSAGES PAR JOUR
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 140 }}>
            {series.map((p) => (
              <div
                key={p.date}
                title={`${p.date} — ${p.value.toLocaleString("fr-FR")} messages`}
                style={{
                  flex: 1,
                  minWidth: 4,
                  height: `${Math.max(2, (p.value / max) * 100)}%`,
                  background: theme.accent,
                  opacity: 0.55,
                  borderRadius: 3,
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, color: theme.ink3, fontSize: 11.5 }}>
            <span>{series[0]?.date}</span>
            <span>
              max {max.toLocaleString("fr-FR")} · {series.at(-1)?.date}
            </span>
          </div>
        </Card>
      )}

      {series.length <= 1 && stats.length > 0 && (
        <Card>
          <div style={{ color: theme.ink3, fontSize: 13 }}>
            Pas encore assez de jours d'activité pour tracer une courbe.
          </div>
        </Card>
      )}
    </div>
  );
}
